// SignInPage — unauthenticated landing page.
// RequireAuth stashes the attempted URL in location.state.from; we thread it
// through to the backend as ?return_to= so WorkOS redirects back after auth.

import { useLocation } from "react-router-dom";
import { LoginButton } from "@avagency/frontend/components/auth/LoginButton";
import { Footer } from "../components/Footer";

interface LocationState {
  from?: { pathname: string; search?: string; hash?: string };
}

function buildSignInUrl(returnTo: string): string {
  const url = new URL("/api/v1/auth/signin", window.location.origin);
  url.searchParams.set("return_to", returnTo);
  return url.toString();
}

// BubbleOrb — small animated orb matching the LP nav-orb language
function BubbleOrb() {
  return (
    <div
      className="relative flex-shrink-0"
      style={{ width: 40, height: 40 }}
      aria-hidden="true"
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          background:
            "radial-gradient(circle at 35% 30%, #f8faff 0%, #a9b8d4 22%, #3b5fa0 48%, #0a1740 75%, #05070d 100%)",
          boxShadow:
            "0 0 0 1px rgba(255,255,255,0.15), 0 4px 20px rgba(59,130,246,0.3)",
          animation: "orb-spin 20s linear infinite",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: "22%",
          borderRadius: "50%",
          background:
            "radial-gradient(circle at 35% 35%, rgba(255,255,255,0.6), transparent 55%)",
          mixBlendMode: "screen" as const,
        }}
      />
    </div>
  );
}

export function SignInPage() {
  const location = useLocation();
  const state = location.state as LocationState | null;
  const from = state?.from;
  const returnTo = from
    ? `${from.pathname}${from.search ?? ""}${from.hash ?? ""}`
    : "/";
  const signInUrl = buildSignInUrl(returnTo);

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-8 p-4">
      {/* Brand lockup */}
      <div className="flex flex-col items-center gap-4 text-center animate-fade-up">
        <BubbleOrb />
        <div className="space-y-1.5">
          <h1
            className="text-2xl font-semibold tracking-tight"
            style={{ letterSpacing: "-0.02em" }}
          >
            Bubble WU Auditor
          </h1>
          <p className="text-sm text-muted-foreground max-w-xs leading-relaxed">
            Workflow Unit usage monitoring and risk alerts for your Bubble apps.
          </p>
        </div>
      </div>

      {/* Sign-in card */}
      <div
        className="w-full max-w-sm animate-fade-up"
        style={{ animationDelay: "60ms" }}
      >
        <div
          className="rounded-xl border p-6 space-y-5"
          style={{
            background:
              "linear-gradient(180deg, hsl(223 55% 10% / 0.8), hsl(222 67% 5% / 0.95))",
            borderColor: "hsl(220 18% 85% / 0.12)",
            boxShadow:
              "0 1px 0 hsl(220 18% 85% / 0.05) inset, 0 20px 40px hsl(222 67% 5% / 0.4)",
          }}
        >
          <div className="space-y-1 text-center">
            <h2 className="text-base font-semibold">Sign in to continue</h2>
            <p className="text-xs text-muted-foreground">
              Use your work email — we'll send a magic link.
            </p>
          </div>

          <LoginButton startUrl={signInUrl} className="w-full" size="default">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 opacity-80"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect width="20" height="16" x="2" y="4" rx="2" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            </svg>
            Sign in with magic link
          </LoginButton>

          <p className="text-center text-xs text-muted-foreground">
            By signing in you agree to our{" "}
            <a
              href="/terms"
              className="underline-offset-2 hover:underline transition-colors hover:text-foreground"
            >
              Terms
            </a>{" "}
            and{" "}
            <a
              href="/privacy"
              className="underline-offset-2 hover:underline transition-colors hover:text-foreground"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>

      <Footer compact />
    </div>
  );
}
